import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DAY_MS } from "@jackedlogs/shared";

import { Badge } from "./Badge";
import type { UiTheme } from "../theme";

// <StaleBadge> — the age of a datum that has stopped arriving (HM-1, 2026-07-15).
//
// THE date+time of the last sample, formatted once. Three surfaces need it
// (MetabolicCard, QuickGlucoseCard, MeasurementTrends), which is exactly when
// the primitive gets built instead of the third copy of an Intl formatter.
//
// It is NOT a third visual expression of "old": it composes the existing
// <Badge>, so the pill's box, radius and tone pairs stay encoded in one place.
//
// tone="neutral", deliberately — NOT FLAG_BADGE.stale's `danger`. That badge
// lives on the coach dashboard, where a stalled athlete is a problem to act on.
// Here the stale datum is the user's own, and DESIGN.md keeps `danger` sealed
// for error/fail/delete. From ~25-07 Antenore has no CGM for over a month, so
// "no fresh data" is the NORMAL state: red on a normal state is red you stop
// seeing within days, and it would spend the error token on a non-error.
// Antenore, 2026-07-15: «per questa ragione ho scelto il tono neutral».
//
// The label is the absolute instant, not "N days ago": his words were «la data
// e ora dell'ultimo dato», and Intl gives that in all five locales. It is NOT a
// fourth `daysAgo` (there are already three copies = 15 strings, and
// CN-relative-age exists to stop the fourth) — no relative age is computed here.
//
// The ONE string it does own is the accessible name. <Badge> only becomes an
// accessible node when given an a11yLabel, so a bare date pill announces
// "15 Jul, 14:32" and nothing else — a naked timestamp with no subject. Sighted
// users read the context off the value sitting beside it; a screen reader gets
// no such adjacency.

export function StaleBadge({
  theme,
  atMs,
  locale,
  cadenceMs,
}: {
  theme: UiTheme;
  /** Instant of the most recent sample. */
  atMs: number;
  /** BCP-47 tag; the badge follows user-i18n, not the runtime default. */
  locale: string;
  /** The series' own rhythm, used only to decide whether the clock time is
   *  information or noise: on a weekly weigh-in "08:12" says nothing, on a
   *  glucose feed it says everything. Null (unknown) reads as coarse. */
  cadenceMs?: number | null;
}) {
  const { t } = useTranslation();
  const fine = cadenceMs != null && cadenceMs < DAY_MS;
  const fmt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        ...(fine ? { hour: "2-digit", minute: "2-digit" } : {}),
      }),
    [locale, fine],
  );
  const at = fmt.format(new Date(atMs));
  return (
    <Badge
      theme={theme}
      tone="neutral"
      label={at}
      a11yLabel={t("common.lastDataAt", {
        defaultValue: "Last data {{at}}",
        at,
      })}
    />
  );
}
